import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pencil, UserPlus } from "lucide-react";
import { memberInvite, memberPatch } from "@shared/schemas/members";
import { validateMember } from "@shared/validation/admin";
import { evaluation, parseInput } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { changedValues, useFormValidation } from "@web/lib/formValidation";
import { recordValidationQuery, useBatchValidation } from "@web/lib/validation";
import { fmtPhone } from "@web/lib/format";
import { api, membersQuery, venuesQuery, type MemberView } from "@web/lib/queries";
import { useMe } from "@web/lib/session";
import { Avatar, BottomSheet, Button, EmptyState, Inline, NavBarAction, Page, Picker, Row, RowGroup, Skeleton, Stack, StatusChip, Text, TextField, Toggle, UnsavedChanges, ValidationBadge, ValidationSummary, useToast } from "@web/ui";
import { AdminsOnly } from "./Settings";

const ROLES = [
  { value: "member", label: "Member" },
  { value: "admin", label: "Admin" },
  { value: "owner", label: "Owner" },
];

/** Settings › Users (SPEC §3.1): list with inline Active toggle → detail sheet; Invite user sheet. */
export function SettingsUsers() {
  const me = useMe();
  const toast = useToast();
  const members = useQuery({ ...membersQuery, enabled: me.companyAdmin });
  const venues = useQuery(venuesQuery(true));
  const checks = useBatchValidation(me.companyAdmin ? (members.data ?? []).map(row => ({ entityType: "member", id: row.id })) : []);
  const [editing, setEditing] = useState<MemberView | null>(null);
  const [inviting, setInviting] = useState(false);
  const validation = useFormValidation(evaluation([], false));
  if (!me.companyAdmin) return <AdminsOnly />;
  const venueSummary = (m: MemberView) => (m.allVenues ? "All venues" : m.venueIds.length ? m.venueIds.map((id) => venues.data?.find((v) => v.id === id)?.shortName ?? "?").join(", ") : "No venues");
  const toggleActive = async (m: MemberView, on: boolean) => {
    if (!validation.begin("member.save")) return;
    try { await api.patchMember(m.id, { active: on }); validation.clear(); }
    catch (e) { validation.reject(e); toast.error(errorMessage(e)); }
    finally { validation.finish(); }
  };

  return (
    <Page>
      <Stack gap={3}>
        {(checks.isError || checks.data?.checksComplete === false) && <ValidationSummary result={checks.data} unavailable onRetry={() => void checks.refetch()} />}
        {(validation.result.issues.length > 0 || validation.unavailable) && <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />}
        {members.isError ? (
          <EmptyState title="Users could not be loaded" body={errorMessage(members.error)} action={<Button onClick={() => void members.refetch()}>Try again</Button>} />
        ) : !members.data ? (
          <Skeleton count={4} />
        ) : (
          <RowGroup>
            {members.data.map((m) => (
              <Row
                key={m.id}
                icon={<Avatar name={m.name} size="sm" />}
                title={m.name}
                subtitle={`${m.email} · ${m.role}${m.active ? "" : " · inactive"} · ${venueSummary(m)}`}
                wrap
                trailing={
                  <Stack gap={1}>
                    {checks.isFetching && !checks.data ? <StatusChip>Checking…</StatusChip> : <ValidationBadge result={checks.data?.records.find(r => r.entity.type === "member" && r.entity.id === m.id)} unavailable={checks.isError} />}
                    <Inline gap={0}>
                    <Toggle label={m.active ? `${m.name} is active` : `${m.name} is inactive`} hideLabel checked={Boolean(m.active)} disabled={m.id === me.member.id || validation.busy} onChange={(v) => { validation.clear(); void toggleActive(m, v); }} />
                    <NavBarAction label={`Edit ${m.name}`} icon={<Pencil aria-hidden size={18} />} onClick={() => setEditing(m)} />
                    </Inline>
                  </Stack>
                }
              />
            ))}
          </RowGroup>
        )}
        <Button variant="secondary" icon={<UserPlus aria-hidden size={18} />} onClick={() => setInviting(true)}>
          Invite user
        </Button>
      </Stack>
      {editing && <MemberSheet key={editing.id} m={members.data?.find((x) => x.id === editing.id) ?? editing} onClose={() => setEditing(null)} />}
      {inviting && <InviteSheet onClose={() => setInviting(false)} />}
    </Page>
  );
}

function MemberSheet({ m, onClose }: { m: MemberView; onClose: () => void }) {
  const toast = useToast();
  const venues = useQuery(venuesQuery(true));
  const saved = useQuery(recordValidationQuery("member", m.id));
  const [f, setF] = useState({ role: m.role, title: m.title ?? "", phone: m.phone ?? "", smsNotify: Boolean(m.smsNotify), emailNotify: Boolean(m.emailNotify), allVenues: Boolean(m.allVenues), venueIds: m.venueIds });
  const [baseline] = useState(f);
  const toBody = (f: typeof baseline) => ({ ...f, title: f.title.trim() || null, phone: f.phone.trim() || null, venueIds: f.allVenues ? [] : f.venueIds });
  const body = changedValues(toBody(f), toBody(baseline));
  const parsed = parseInput(memberPatch, body, { type: "member", id: m.id }, "member.save");
  const validation = useFormValidation(evaluation([...parsed.issues, ...validateMember({ ...m, ...body }, { before: m, changedFields: Object.keys(body) }).issues], false));
  const { busy, field } = validation;
  const set = (next: typeof f) => validation.edit(() => setF(next));

  async function save() {
    if (!parsed.success || !validation.begin("member.save")) return;
    try {
      await api.patchMember(m.id, parsed.data);
      toast.success("User saved");
      onClose();
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open
      onClose={() => { if (!busy) onClose(); }}
      title={m.name}
      footer={
        <Button loading={busy} onClick={save}>
          Save user
        </Button>
      }
    >
      <Stack gap={3}>
        <UnsavedChanges dirty={!busy && Object.keys(body).length > 0} />
        <ValidationSummary result={saved.data} loading={saved.isFetching} unavailable={saved.isError} onRetry={() => void saved.refetch()} saved />
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        <Inline gap={3}>
          <Avatar name={m.name} />
          <Stack gap={0}>
            <Text variant="headline">{m.name}</Text>
            <Text variant="callout" tone="secondary">
              {m.email}
            </Text>
          </Stack>
          {m.platformAdmin && <StatusChip tone="accent">Platform admin</StatusChip>}
        </Inline>
        <Picker label="Role" value={f.role} onChange={(v) => set({ ...f, role: v as typeof f.role })} options={ROLES} hint="Owners and admins manage settings, users, refunds" {...field("role")} />
        <TextField label="Title" value={f.title} onChange={(e) => set({ ...f, title: e.target.value })} {...field("title")} />
        <TextField label="Phone" type="tel" value={f.phone} onChange={(e) => set({ ...f, phone: e.target.value })} hint={m.phone ? `Stored as ${fmtPhone(m.phone)}` : undefined} {...field("phone")} />
        <Toggle label="SMS notifications" checked={f.smsNotify} onChange={(v) => set({ ...f, smsNotify: v })} disabled={busy} />
        <Toggle label="Email notifications" checked={f.emailNotify} onChange={(v) => set({ ...f, emailNotify: v })} disabled={busy} />
        <Toggle label="All venues" description="Off: pick which venues this user sees" checked={f.allVenues} onChange={(v) => set({ ...f, allVenues: v })} disabled={busy} />
        {!f.allVenues &&
          (venues.data ?? []).map((v) => (
            <Toggle key={v.id} label={v.name} checked={f.venueIds.includes(v.id)} disabled={busy} onChange={(on) => set({ ...f, venueIds: on ? [...f.venueIds, v.id] : f.venueIds.filter((x) => x !== v.id) })} />
          ))}
      </Stack>
    </BottomSheet>
  );
}

function InviteSheet({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [f, setF] = useState({ name: "", email: "", role: "member", phone: "" });
  const parsed = parseInput(memberInvite, { ...f, phone: f.phone.trim() || null }, { type: "member" }, "member.save");
  const validation = useFormValidation(evaluation(parsed.issues, false));
  const { busy, field } = validation;
  const set = (next: typeof f) => validation.edit(() => setF(next));

  async function save() {
    if (!parsed.success || !validation.begin("member.save")) return;
    try {
      const m = await api.inviteMember(parsed.data);
      toast.success(`${m.name} can now sign in with ${m.email}`);
      onClose();
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open
      onClose={() => { if (!busy) onClose(); }}
      title="Invite user"
      footer={
        <Button loading={busy} onClick={save}>
          Invite
        </Button>
      }
    >
      <Stack gap={3}>
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        <UnsavedChanges dirty={!busy && Boolean(f.name || f.email || f.phone || f.role !== "member")} />
        <Text variant="callout" tone="secondary">
          They sign in with Google or an emailed link using this address. No email is sent now.
        </Text>
        <TextField label="Name" value={f.name} onChange={(e) => set({ ...f, name: e.target.value })} autoFocus {...field("name")} />
        <TextField label="Email" type="email" inputMode="email" autoCapitalize="none" value={f.email} onChange={(e) => set({ ...f, email: e.target.value })} {...field("email")} />
        <Picker label="Role" value={f.role} onChange={(v) => set({ ...f, role: v })} options={ROLES} {...field("role")} />
        <TextField label="Phone" type="tel" value={f.phone} onChange={(e) => set({ ...f, phone: e.target.value })} {...field("phone")} />
      </Stack>
    </BottomSheet>
  );
}
